'use client';

/* eslint-disable no-restricted-syntax */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useFilter, useId } from 'react-aria';
import type { Node } from 'react-stately';
import { usePrevious } from 'hooks/usePrevious';
import { useDensity } from 'theme-utils';
import { useListState } from '../hooks/useListState';
import type {
  ListReducerFilterActions,
  ListReducerFilterState,
  ListStateProps,
} from './types';

type ReducerState = ListReducerFilterState & {
  effect?: () => any;
};

const DEBOUNCE_DELAY = 500;

/**
 * Create a list collection that can optionally be filtered either locally or remotely.
 * Wraps react-stately's useListState hook to add filtering capabilities.
 *
 * Local filtering: Filters items client-side based on text matching
 * Remote filtering: Calls onRequestSearch prop to trigger server-side filtering
 *
 * @link https://react-spectrum.adobe.com/react-stately/useListState.html
 *
 * @example
 * // Local filtering
 * const { state } = useFilteredListState({
 *   items: myItems,
 *   children: (item) => <ListItem key={item.id}>{item.name}</ListItem>
 * });
 *
 * // Remote filtering
 * const { state } = useFilteredListState({
 *   items: myItems,
 *   onRequestSearch: (query) => fetchFilteredItems(query),
 *   children: (item) => <ListItem key={item.id}>{item.name}</ListItem>
 * });
 */

export function useFilteredListState<T extends object>(
  props: ListStateProps<T>
) {
  const id = useId();
  const isAsync = Boolean(props.onRequestSearch);
  const debouncedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [queryState, dispatchQuery] = useReducer(
    (state: ReducerState, action: ListReducerFilterActions) => {
      if (action.type === 'query') {
        /**
         * If there is an existing timeout, clear it
         */
        if (debouncedTimerRef.current) {
          clearTimeout(debouncedTimerRef.current);
          debouncedTimerRef.current = null;
        }

        /**
         * This is the most common action, and is used to update the query state.
         */
        const nextState: ReducerState = {
          ...state,
          effect: undefined,
          query: action.state,
          hasQueryChanged: true,
        };

        /**
         * For synchronous filtering immediately return the next state
         */
        if (!isAsync) {
          return nextState;
        }

        /**
         * For async filtering, simulate debouncing behavior
         */
        return {
          ...nextState,
          debouncing: true,
          effect() {
            // Empty queries should trigger immediately
            if (!action.state?.trim()) {
              dispatchQuery({ type: 'debounce-end' });
              return undefined;
            }

            // Sets reference to set timeout for cleanup when hook unmounts
            debouncedTimerRef.current = setTimeout(() => {
              dispatchQuery({ type: 'debounce-end' });
            }, DEBOUNCE_DELAY);

            return undefined;
          },
        };
      }

      if (action.type === 'toggle-focus') {
        return {
          ...state,
          focused: !state.focused,
        };
      }

      if (action.type === 'clear-query') {
        return {
          ...state,
          effect: undefined,
          debouncing: false,
          hasQueryChanged: true,
          query: '',
        };
      }

      if (action.type === 'debounce-end') {
        /**
         * This event is triggered when the debounce action is completed
         * and we trigger user-defined event hooks with the current query state
         */
        return {
          ...state,
          effect() {
            props.onRequestSearch?.(state.query);
            dispatchQuery({ type: 'search-start' });
          },
        };
      }

      if (action.type === 'search-start') {
        return {
          ...state,
          effect: undefined,
          debouncing: false,
        };
      }

      return state;
    },
    {
      debouncing: false,
      query: props.defaultQuery ?? '', // handle a prefilled query prop
      focused: false,
      hasQueryChanged: false,
    }
  );

  /**
   * This effect is used to trigger the debounced search when the query state changes.
   */
  useEffect(() => {
    if (queryState.effect) {
      const effect = queryState.effect;
      delete queryState.effect;
      return effect();
    }
    return undefined;
  });

  useEffect(() => {
    return () => {
      if (debouncedTimerRef.current) {
        clearTimeout(debouncedTimerRef.current);
      }
    };
  }, []);

  const { contains } = useFilter({ sensitivity: 'base' });

  /**
   * When locally filtering use this function to filter both
   * the items and the sections (if they do not contain any items).
   * When filtering remotely, omit this and let the consumer
   * handle the filtering.
   *
   * Filter logic based upon: https://github.com/adobe/react-spectrum/blob/109ea066ed6d953abd2ee2cd7f8051823ae7cf52/packages/%40react-stately/combobox/src/useComboBoxState.ts#L378
   */
  const clientFilter = useCallback(
    (nodes: Iterable<Node<T>>): Node<T>[] => {
      const filteredNodes: Node<T>[] = [];

      for (const node of nodes) {
        if (node.type === 'section' && node.hasChildNodes) {
          // Recursively filter section children
          const filtered = clientFilter(node.childNodes);
          if ([...filtered].some((n) => n.type === 'item')) {
            filteredNodes.push({ ...node, childNodes: filtered });
          }
        } else if (
          node.type === 'item' &&
          contains(node.textValue, queryState.query)
        ) {
          // Keep matching items
          filteredNodes.push({ ...node });
        } else if (node.type !== 'item') {
          // Keep non-item nodes
          filteredNodes.push({ ...node });
        }
      }

      return filteredNodes;
    },
    [contains, queryState.query]
  );

  const enableLocalFilter = !isAsync && queryState.query;

  const density = useDensity();
  const context = useMemo(
    () => ({ variant: props.variant, density }),
    [props.variant, density]
  );

  const listState = useListState(
    {
      ...props,
      filter: enableLocalFilter ? clientFilter : undefined,
    },
    context
  );

  const isLoadingVisible =
    (isAsync && queryState.debouncing) || props.loading === 'loading';

  const prevLoadingVisible = usePrevious(isLoadingVisible);

  return {
    state: {
      ...listState,
      queryState,
      dispatchQuery,
      isAsync,
      id,
      prevLoadingVisible,
      isLoadingVisible,
    },
  };
}
